import type { ServerFilters } from "@/hooks/useQueryFilters";
import type { RowData, Table, TableFeature } from "@tanstack/react-table";

export interface ServerSideFilteringState {
  serverSideFilters?: ServerFilters<string>;
  initialServerSideFilters?: ServerFilters<string>;
  initialGlobalServerSideSearch?: string;
  globalServerSideSearch?: string;
}

export interface ServerSideFilteringOptions {
  onServerSideFiltersClear?: () => void;
  initialServerSideFilters?: ServerFilters<string>;
  initialGlobalServerSideSearch?: string;
}

export interface ServerSideFilteringInstance {
  hasActiveServerSideFilters: () => boolean;
  clearServerSideFilters: () => void;
  getServerSideFilters: () => ServerFilters<string> | undefined;
}

declare module "@tanstack/react-table" {
  interface TableState extends ServerSideFilteringState {}
  interface InitialTableState extends ServerSideFilteringState {}
  interface TableOptionsResolved<TData extends RowData>
    extends ServerSideFilteringOptions {}
  interface Table<TData extends RowData> extends ServerSideFilteringInstance {}
}

export const ServerSideFilteringFeature: TableFeature<any> = {
  getInitialState: (state): ServerSideFilteringState => {
    return {
      serverSideFilters: undefined,
      ...state,
    };
  },

  getDefaultOptions: (): ServerSideFilteringOptions => {
    return {
      onServerSideFiltersClear: undefined,
      initialServerSideFilters: undefined,
      initialGlobalServerSideSearch: undefined,
    };
  },

  createTable: <TData extends RowData>(table: Table<TData>): void => {
    table.getServerSideFilters = () => {
      return table.getState().serverSideFilters;
    };

    table.hasActiveServerSideFilters = () => {
      const currentFilters = table.getState().serverSideFilters;
      const initialFilters = table.options.initialServerSideFilters;
      const currentSearch = table.getState().globalServerSideSearch ?? "";
      const initialSearch = table.options.initialGlobalServerSideSearch ?? "";

      const filtersChanged =
        JSON.stringify(currentFilters) !== JSON.stringify(initialFilters);

      const searchChanged = currentSearch !== initialSearch;

      return filtersChanged || searchChanged;
    };

    table.clearServerSideFilters = () => {
      table.options.onServerSideFiltersClear?.();
    };
  },
};
